<template>
  <div v-if="isVisible" class="cpd-overlay" @click.self="close">
    <div aria-labelledby="cpd-title" aria-modal="true" class="cpd-dialog" role="dialog">
      <div class="cpd-header">
        <div class="cpd-header-text">
          <h3 id="cpd-title" class="cpd-title">{{ $t('general.connection_check') }}</h3>
          <p v-if="contentTitle" class="cpd-subtitle">{{ contentTitle }}</p>
        </div>
        <button :aria-label="$t('general.close')" class="cpd-close" type="button" @click="close">
          <i class="uil-times"></i>
        </button>
      </div>

      <div class="cpd-progress">
        <div class="cpd-progress-meta">
          <span>{{ isSequencing ? $t('general.checking') : $t('general.completed') }}</span>
          <strong>{{ completedCount }}/{{ steps.length }}</strong>
        </div>
        <div class="cpd-progress-track">
          <div :style="{ width: progressPercent + '%' }" class="cpd-progress-fill"></div>
        </div>
      </div>

      <div class="cpd-body">
        <ol class="cpd-steps">
          <li v-for="(step, index) in steps"
              :key="step.id"
              :class="[
              'is-' + step.status,
              { 'is-active': step.status === 'running' || step.status === 'awaiting' },
              { 'is-revealed': step.status !== 'waiting' },
            ]"
              class="cpd-step"
          >
            <div class="cpd-step-rail">
              <div class="cpd-step-badge">
                <i v-if="step.status === 'running'" class="uil-spinner cpd-spin"></i>
                <i v-else-if="step.status === 'ok'" class="uil-check"></i>
                <i v-else-if="step.status === 'warn'" class="uil-exclamation"></i>
                <i v-else-if="step.status === 'bad'" class="uil-times"></i>
                <i v-else-if="step.status === 'awaiting'" class="uil-volume"></i>
                <span v-else>{{ index + 1 }}</span>
              </div>
              <div v-if="index < steps.length - 1" class="cpd-step-line"></div>
            </div>

            <div class="cpd-step-content">
              <div class="cpd-step-head">
                <div class="cpd-step-title">{{ step.title }}</div>
                <div class="cpd-step-state">{{ stepStateLabel(step) }}</div>
              </div>

              <div v-if="step.status !== 'waiting'" :class="'cpd-detail-' + step.id" class="cpd-step-detail">
                <template v-if="step.id !== 'audio'">
                  <div class="cpd-detail-row">
                    <span :class="'is-' + step.status" class="cpd-detail-icon">
                      <i :class="stepIcon(step)"></i>
                    </span>
                    <div class="cpd-detail-text">
                      <div v-if="step.value" class="cpd-step-value">{{ step.value }}</div>
                      <div v-if="step.hint" class="cpd-step-hint">{{ step.hint }}</div>
                    </div>
                  </div>
                </template>

                <template v-else>
                  <div class="cpd-detail-row">
                    <span :class="'is-' + step.status" class="cpd-detail-icon is-audio">
                      <i class="uil-volume"></i>
                    </span>
                    <div class="cpd-detail-text">
                      <div v-if="step.value" class="cpd-step-value">{{ step.value }}</div>
                      <div v-if="step.hint" class="cpd-step-hint">{{ step.hint }}</div>
                    </div>
                  </div>

                  <div v-if="['awaiting', 'ok', 'bad'].includes(step.status)"
                       class="cpd-audio-actions">
                    <button class="cpd-audio-btn cpd-audio-btn-play" type="button" @click="playBeep">
                      <i class="uil-play"></i> {{ $t('general.play_beep') }}
                    </button>
                    <button class="cpd-audio-btn" type="button" @click="confirmAudio(true)">
                      <i class="uil-volume"></i> {{ $t('general.heard') }}
                    </button>
                    <button class="cpd-audio-btn" type="button" @click="confirmAudio(false)">
                      <i class="uil-volume-mute"></i> {{ $t('general.not_heard') }}
                    </button>
                  </div>

                  <div class="cpd-waveform">
                    <div aria-hidden="true" class="cpd-waveform-bars">
                      <span v-for="n in 48"
                            :key="n"
                            :style="{ height: waveBarHeight(n) + '%' }"
                            class="cpd-wave-bar"
                      ></span>
                    </div>
                    <span class="cpd-waveform-time">00:00 / 00:02</span>
                  </div>
                </template>
              </div>
            </div>
          </li>
        </ol>
      </div>

      <div class="cpd-footer">
        <button :disabled="isSequencing" class="cpd-btn cpd-btn-secondary" type="button" @click="runAllChecks">
          <i class="uil-refresh"></i> {{ $t('general.remeasure') }}
        </button>
        <button class="cpd-btn cpd-btn-primary" type="button" @click="refreshPage">
          <i class="uil-refresh"></i> {{ $t('general.refresh_page') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
const SPEED_TEST_PATH = '/docs/assets/teams/teams-01.png';
const SPEED_ROUNDS = 3;
const STEP_PAUSE_MS = 400;

export default {
  name: 'ContentPlaybackDiagnostics',
  props: {
    contentTitle: { type: String, default: '' },
  },
  data() {
    return {
      isVisible: false,
      isSequencing: false,
      sequenceToken: 0,
      isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
      speedMbps: null,
      viewportWidth: 0,
      viewportHeight: 0,
      steps: [],
      _onlineHandler: null,
      _offlineHandler: null,
    };
  },
  computed: {
    completedCount() {
      return this.steps.filter((s) => ['ok', 'warn', 'bad'].includes(s.status)).length;
    },
    progressPercent() {
      if (!this.steps.length) return 0;
      const weighted = this.steps.reduce((sum, step) => {
        if (['ok', 'warn', 'bad'].includes(step.status)) return sum + 1;
        if (step.status === 'running' || step.status === 'awaiting') return sum + 0.45;
        return sum;
      }, 0);
      return Math.round((weighted / this.steps.length) * 100);
    },
  },
  beforeDestroy() {
    this.sequenceToken += 1;
    this.unbindNetworkListeners();
  },
  methods: {
    show() {
      this.isVisible = true;
      this.bindNetworkListeners();
      this.runAllChecks();
    },
    close() {
      this.sequenceToken += 1;
      this.isSequencing = false;
      this.isVisible = false;
      this.unbindNetworkListeners();
      this.$emit('close');
    },
    bindNetworkListeners() {
      if (this._onlineHandler) return;
      this._onlineHandler = () => {
        this.isOnline = true;
      };
      this._offlineHandler = () => {
        this.isOnline = false;
      };
      window.addEventListener('online', this._onlineHandler);
      window.addEventListener('offline', this._offlineHandler);
    },
    unbindNetworkListeners() {
      if (!this._onlineHandler) return;
      window.removeEventListener('online', this._onlineHandler);
      window.removeEventListener('offline', this._offlineHandler);
      this._onlineHandler = null;
      this._offlineHandler = null;
    },
    buildSteps() {
      return [
        { id: 'connection', title: this.$t('general.connection'), status: 'waiting', value: '', hint: '' },
        { id: 'speed', title: this.$t('general.download_speed'), status: 'waiting', value: '', hint: '' },
        { id: 'screen', title: this.$t('general.screen'), status: 'waiting', value: '', hint: '' },
        { id: 'audio', title: this.$t('general.audio'), status: 'waiting', value: '', hint: '' },
      ];
    },
    stepIcon(step) {
      if (step.id === 'connection') return 'uil-globe';
      if (step.id === 'speed') return 'uil-tachometer-fast';
      if (step.id === 'screen') return 'uil-desktop';
      if (step.id === 'audio') return 'uil-volume';
      return 'uil-info-circle';
    },
    waveBarHeight(n) {
      const pattern = [28, 42, 55, 70, 48, 62, 38, 80, 45, 58, 72, 40, 65, 50, 85, 35];
      return pattern[(n - 1) % pattern.length];
    },
    stepStateLabel(step) {
      if (step.status === 'waiting') return this.$t('general.waiting');
      if (step.status === 'running') return this.$t('general.checking_status');
      if (step.status === 'awaiting') return this.$t('general.awaiting_confirmation');
      if (step.status === 'ok') return this.$t('general.ok');
      if (step.status === 'warn') return this.$t('general.warning');
      if (step.status === 'bad') return this.$t('general.issue');
      return '';
    },
    getStep(id) {
      return this.steps.find((s) => s.id === id);
    },
    patchStep(id, patch) {
      const step = this.getStep(id);
      if (!step) return;
      Object.assign(step, patch);
    },
    sleep(ms, token) {
      return new Promise((resolve) => {
        setTimeout(() => resolve(token === this.sequenceToken), ms);
      });
    },
    async runAllChecks() {
      const token = ++this.sequenceToken;
      this.isSequencing = true;
      this.speedMbps = null;
      this.steps = this.buildSteps();

      await this.$nextTick();
      if (!(await this.sleep(150, token))) return;

      for (let i = 0; i < this.steps.length; i += 1) {
        if (token !== this.sequenceToken) return;
        const { id } = this.steps[i];
        this.patchStep(id, { status: 'running', value: '', hint: '' });

        if (id === 'connection') await this.runConnectionStep(token);
        else if (id === 'speed') await this.runSpeedStep(token);
        else if (id === 'screen') await this.runScreenStep(token);
        else if (id === 'audio') await this.runAudioStep(token);

        if (token !== this.sequenceToken) return;
        if (i < this.steps.length - 1) await this.sleep(STEP_PAUSE_MS, token);
      }

      if (token === this.sequenceToken) this.isSequencing = false;
    },
    async runConnectionStep(token) {
      await this.sleep(400, token);
      if (token !== this.sequenceToken) return;
      this.isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
      this.patchStep('connection', {
        status: this.isOnline ? 'ok' : 'bad',
        value: this.isOnline ? this.$t('general.online') : this.$t('general.offline'),
        hint: this.isOnline ? '' : this.$t('general.check_wifi_or_mobile_data'),
      });
    },
    async runSpeedStep(token) {
      this.patchStep('speed', { status: 'running', value: this.$t('general.measuring'), hint: '' });
      const mbps = await this.measureSpeed();
      if (token !== this.sequenceToken) return;
      this.speedMbps = mbps;
      this.applySpeedResult(mbps);
    },
    applySpeedResult(mbps) {
      if (mbps == null) {
        this.patchStep('speed', { status: 'bad', value: this.$t('general.could_not_measure'), hint: '' });
        return;
      }
      const level = mbps >= 5 ? 'good' : mbps >= 1.5 ? 'fair' : 'poor';
      this.patchStep('speed', {
        status: level === 'good' ? 'ok' : level === 'fair' ? 'warn' : 'bad',
        value: `${mbps.toFixed(1)} Mbps`,
        hint: level === 'good'
            ? ''
            : level === 'fair'
                ? this.$t('general.speed_fair_hint')
                : this.$t('general.speed_poor_hint'),
      });
    },
    async runScreenStep(token) {
      await this.sleep(300, token);
      if (token !== this.sequenceToken) return;
      this.viewportWidth = window.innerWidth || 0;
      this.viewportHeight = window.innerHeight || 0;
      const ok = this.viewportWidth >= 360;
      this.patchStep('screen', {
        status: ok ? 'ok' : 'warn',
        value: `${this.viewportWidth} × ${this.viewportHeight}`,
        hint: ok ? '' : this.$t('general.screen_narrow_hint'),
      });
    },
    async runAudioStep(token) {
      this.patchStep('audio', { status: 'running', value: '', hint: '' });
      await this.sleep(250, token);
      if (token !== this.sequenceToken) return;
      await this.playBeep();
      if (token !== this.sequenceToken) return;
      this.patchStep('audio', {
        status: 'awaiting',
        value: this.$t('general.beep_played'),
        hint: this.$t('general.audio_confirm_hint'),
      });
    },
    confirmAudio(heard) {
      this.patchStep('audio', {
        status: heard ? 'ok' : 'bad',
        value: heard ? this.$t('general.audio_heard') : this.$t('general.audio_not_heard'),
        hint: heard ? '' : this.$t('general.check_device_audio'),
      });
    },
    async measureSpeed() {
      try {
        let totalBytes = 0;
        const start = performance.now();
        for (let i = 0; i < SPEED_ROUNDS; i += 1) {
          const res = await fetch(`${SPEED_TEST_PATH}?cb=${Date.now()}_${i}`, {
            cache: 'no-store',
            credentials: 'same-origin',
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const blob = await res.blob();
          totalBytes += blob.size;
        }
        const seconds = (performance.now() - start) / 1000;
        if (seconds <= 0 || totalBytes <= 0) throw new Error('invalid');
        let mbps = Math.max(0.1, (totalBytes * 8) / seconds / 1e6);
        const reported = navigator.connection && navigator.connection.downlink;
        if (typeof reported === 'number' && reported > 0 && mbps > 50 && reported < mbps) {
          mbps = reported;
        }
        return mbps;
      } catch (e) {
        const reported = navigator.connection && navigator.connection.downlink;
        return typeof reported === 'number' && reported > 0 ? reported : null;
      }
    },
    async playBeep() {
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) return;
        const ctx = new AudioCtx();
        if (ctx.state === 'suspended') await ctx.resume();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = 880;
        gain.gain.value = 0.12;
        osc.connect(gain);
        gain.connect(ctx.destination);
        const now = ctx.currentTime;
        osc.start(now);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
        osc.stop(now + 0.4);
        setTimeout(() => {
          ctx.close().catch(() => {
          });
        }, 500);
      } catch (e) { /* ignore */
      }
    },
    refreshPage() {
      window.location.reload();
    },
  },
};
</script>

<style scoped>
.cpd-overlay {
  position: fixed;
  inset: 0;
  z-index: 2147483646;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.cpd-dialog {
  width: 100%;
  max-width: 720px;
  max-height: min(92vh, 780px);
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.28);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.cpd-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 20px 20px 12px;
}

.cpd-header-text {
  flex: 1;
  min-width: 0;
}

.cpd-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.3;
}

.cpd-subtitle {
  margin: 4px 0 0;
  font-size: 13px;
  color: #94a3b8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cpd-close {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: #f1f5f9;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 18px;
}

.cpd-close:hover {
  background: #e2e8f0;
  color: #334155;
}

.cpd-progress {
  padding: 0 20px 14px;
}

.cpd-progress-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 8px;
}

.cpd-progress-meta strong {
  color: #1e293b;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.cpd-progress-track {
  height: 8px;
  border-radius: 999px;
  background: #e2e8f0;
  overflow: hidden;
}

.cpd-progress-fill {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #3b82f6 0%, #22c55e 100%);
  transition: width 0.35s ease;
}

.cpd-body {
  padding: 8px 20px 4px;
  overflow: auto;
  flex: 1;
}

.cpd-steps {
  list-style: none;
  margin: 0;
  padding: 0;
}

.cpd-step {
  display: flex;
  gap: 12px;
  opacity: 0.4;
  transition: opacity 0.25s ease;
}

.cpd-step.is-revealed,
.cpd-step.is-active {
  opacity: 1;
}

.cpd-step-rail {
  width: 28px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.cpd-step-badge {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 2px solid #cbd5e1;
  background: #fff;
  color: #94a3b8;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  z-index: 1;
  flex-shrink: 0;
}

.cpd-step.is-running .cpd-step-badge,
.cpd-step.is-awaiting .cpd-step-badge {
  border-color: #3b82f6;
  color: #2563eb;
  background: #eff6ff;
}

.cpd-step.is-ok .cpd-step-badge {
  border-color: #22c55e;
  background: #22c55e;
  color: #fff;
}

.cpd-step.is-warn .cpd-step-badge {
  border-color: #f59e0b;
  background: #f59e0b;
  color: #fff;
}

.cpd-step.is-bad .cpd-step-badge {
  border-color: #ef4444;
  background: #ef4444;
  color: #fff;
}

.cpd-step-line {
  width: 2px;
  flex: 1;
  min-height: 20px;
  background: #e2e8f0;
  margin: 4px 0;
}

.cpd-step.is-ok .cpd-step-line,
.cpd-step.is-warn .cpd-step-line,
.cpd-step.is-bad .cpd-step-line {
  background: #d1fae5;
}

.cpd-step-content {
  flex: 1;
  min-width: 0;
  padding-bottom: 16px;
}

.cpd-step-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
}

.cpd-step-title {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
}

.cpd-step-state {
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
  white-space: nowrap;
}

.cpd-step.is-running .cpd-step-state,
.cpd-step.is-awaiting .cpd-step-state {
  color: #2563eb;
}

.cpd-step.is-ok .cpd-step-state {
  color: #16a34a;
}

.cpd-step.is-warn .cpd-step-state {
  color: #d97706;
}

.cpd-step.is-bad .cpd-step-state {
  color: #dc2626;
}

.cpd-step-detail {
  padding: 12px 14px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.cpd-step.is-ok .cpd-step-detail {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.cpd-step.is-warn .cpd-step-detail {
  background: #fffbeb;
  border-color: #fde68a;
}

.cpd-step.is-bad .cpd-step-detail {
  background: #fef2f2;
  border-color: #fecaca;
}

.cpd-step.is-running .cpd-step-detail,
.cpd-step.is-awaiting .cpd-step-detail {
  background: #eff6ff;
  border-color: #bfdbfe;
}

.cpd-detail-audio {
  background: #f1f5f9 !important;
  border-color: #e2e8f0 !important;
}

.cpd-step.is-ok .cpd-detail-audio {
  background: #f0f9ff !important;
  border-color: #bae6fd !important;
}

.cpd-step.is-awaiting .cpd-detail-audio {
  background: #f1f5f9 !important;
  border-color: #e2e8f0 !important;
}

.cpd-detail-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.cpd-detail-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 16px;
  background: #e2e8f0;
  color: #64748b;
}

.cpd-detail-icon.is-ok {
  background: #dcfce7;
  color: #16a34a;
}

.cpd-detail-icon.is-warn {
  background: #fef3c7;
  color: #d97706;
}

.cpd-detail-icon.is-bad {
  background: #fee2e2;
  color: #dc2626;
}

.cpd-detail-icon.is-running,
.cpd-detail-icon.is-awaiting,
.cpd-detail-icon.is-audio.is-awaiting {
  background: #dbeafe;
  color: #2563eb;
}

.cpd-detail-icon.is-audio.is-ok {
  background: #dbeafe;
  color: #2563eb;
}

.cpd-detail-text {
  min-width: 0;
  flex: 1;
}

.cpd-step-value {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.3;
}

.cpd-step-hint {
  margin-top: 2px;
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.4;
}

.cpd-audio-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.cpd-audio-btn {
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  line-height: 1.2;
  background: #fff;
  color: #334155;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.cpd-audio-btn:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.cpd-audio-btn-play {
  border-color: #86efac;
  color: #16a34a;
}

.cpd-audio-btn-play:hover {
  background: #f0fdf4;
  border-color: #4ade80;
}

.cpd-waveform {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid rgba(148, 163, 184, 0.25);
}

.cpd-waveform-bars {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 2px;
  height: 28px;
  min-width: 0;
}

.cpd-wave-bar {
  flex: 1;
  min-width: 2px;
  max-width: 4px;
  border-radius: 2px;
  background: #cbd5e1;
  opacity: 0.85;
}

.cpd-waveform-time {
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  flex-shrink: 0;
}

.cpd-footer {
  display: flex;
  gap: 10px;
  padding: 14px 20px 20px;
  background: #fff;
}

.cpd-btn {
  border: none;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  line-height: 1.2;
  flex: 1;
  justify-content: center;
}

.cpd-btn:disabled {
  opacity: 0.55;
  cursor: default;
}

.cpd-btn-primary {
  background: #2563eb;
  color: #fff;
}

.cpd-btn-primary:hover:not(:disabled) {
  background: #1d4ed8;
}

.cpd-btn-secondary {
  background: #f1f5f9;
  color: #1e293b;
}

.cpd-btn-secondary i {
  color: #16a34a;
}

.cpd-btn-secondary:hover:not(:disabled) {
  background: #e2e8f0;
}

.cpd-spin {
  display: inline-block;
  animation: cpd-spin 0.8s linear infinite;
}

@keyframes cpd-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 480px) {
  .cpd-overlay {
    padding: 10px;
    align-items: flex-end;
  }

  .cpd-dialog {
    max-height: 92vh;
    border-radius: 16px 16px 12px 12px;
  }

  .cpd-header,
  .cpd-progress,
  .cpd-body,
  .cpd-footer {
    padding-left: 16px;
    padding-right: 16px;
  }

  .cpd-footer {
    flex-direction: column;
    padding-bottom: 16px;
  }

  .cpd-audio-actions {
    flex-direction: column;
  }

  .cpd-audio-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
